import React, { useMemo, useState } from 'react';
import { Alert, Button, Empty, Space, Table, Tooltip, Typography } from 'antd';
import { InfoCircleOutlined, ReloadOutlined } from '@ant-design/icons';
import {
  LokasyonBagi, NEGATIF_RENK, SAYISAL_TIPLER, kolonGenisligi, para, sayi, tipBicim, useAktifYukle,
} from './biOrtak';

const { Text } = Typography;
const SABIT_SOL = new Set(['Lokasyon No', 'Lokasyon Adı']);

/**
 * Satırlar sekmesi: /bi-v2/{key}/satirlar — sunucu tarafında sayfalı ve sıralı ham satırlar (tüm kolonlar).
 * Süzgeçler sayfanın üstündeki çubuktan gelir; burada yalnız sayfa / sıralama tutulur.
 */
export default function BiSatirlar({ api, rapor, params, aktif, surum }) {
  const [boy, setBoy] = useState(100);
  // Süzgeç / rapor değişince ilk sayfaya, rapor değişince varsayılan sıralamaya dönülür. Durum hangi anahtar için
  // tutulduğuyla birlikte saklanır; böylece eski sayfa numarasıyla gereksiz bir istek atılmaz.
  const suzgecAnahtari = rapor ? JSON.stringify([rapor.key, params]) : null;
  const [sayfaDurum, setSayfaDurum] = useState({ a: null, s: 1 });
  const sayfa = sayfaDurum.a === suzgecAnahtari ? sayfaDurum.s : 1;
  const setSayfa = (s) => setSayfaDurum({ a: suzgecAnahtari, s });
  const [sira, setSira] = useState({ rk: null, kolon: null, yon: 'azalan' });
  const sirala = sira.rk === rapor?.key ? sira.kolon : null;
  const yon = sira.rk === rapor?.key ? sira.yon : 'azalan';

  const istek = useMemo(() => ({
    offset: (sayfa - 1) * boy, limit: boy, sirala: sirala || undefined, yon, ...params,
  }), [sayfa, boy, sirala, yon, params]);
  const anahtar = rapor ? JSON.stringify([rapor.key, istek, surum]) : null;
  const { veri, yukleniyor, hata: hataMetni, yeniden } = useAktifYukle(aktif, anahtar,
    () => api.rapor(rapor.key, 'satirlar', istek));

  const meta = useMemo(() => veri?.kolonlar || rapor?.kolonlar || [], [veri, rapor]);
  const kolonlar = useMemo(() => meta.map((k) => {
    const sayisal = SAYISAL_TIPLER.has(k.tip);
    return {
      title: k.aciklama ? <Tooltip title={k.aciklama}>{k.ad} <InfoCircleOutlined style={{ color: '#8c8c8c' }} /></Tooltip> : k.ad,
      key: k.ad,
      width: kolonGenisligi(k),
      align: sayisal ? 'right' : undefined,
      fixed: SABIT_SOL.has(k.ad) ? 'left' : undefined,
      ellipsis: { showTitle: true },
      sorter: true,
      sortOrder: sirala === k.ad ? (yon === 'artan' ? 'ascend' : 'descend') : null,
      render: (_, r) => {
        const v = r[k.ad];
        if (k.ad === 'Lokasyon No') return <LokasyonBagi no={v} />;
        if (k.tip === 'tutar') {
          return <span className="nf-amount" style={v < 0 ? { color: NEGATIF_RENK } : undefined}>{para(v)}</span>;
        }
        return tipBicim(v, k.tip);
      },
    };
  }), [meta, sirala, yon]);
  const genislik = useMemo(() => kolonlar.reduce((t, k) => t + (k.width || 150), 0), [kolonlar]);
  const satirlar = useMemo(() => (veri?.satirlar || []).map((r, i) => ({ ...r, __k: istek.offset + i })), [veri, istek.offset]);

  const degisti = (p, _f, s) => {
    if (p.pageSize !== boy) {
      setBoy(p.pageSize);
      setSayfa(1);
    } else {
      setSayfa(p.current);
    }
    const kolon = s && !Array.isArray(s) && s.order ? s.columnKey : null;
    const yeniYon = s?.order === 'ascend' ? 'artan' : 'azalan';
    if (kolon !== sirala || (kolon && yeniYon !== yon)) {
      setSira({ rk: rapor?.key, kolon, yon: yeniYon });
      setSayfa(1);
    }
  };

  return (
    <div>
      <Space wrap style={{ marginBottom: 8 }}>
        {veri && (
          <>
            <Text strong>{sayi(veri.toplam_satir)} satır</Text>
            <Text>Σ Tutar: <Text strong className="nf-amount">{para(veri.toplam_tutar)}</Text></Text>
          </>
        )}
        <Button icon={<ReloadOutlined />} onClick={yeniden} loading={yukleniyor}>Yeniden yükle</Button>
        <Text type="secondary">Sıralama ve sayfalama sunucuda yapılır; toplam süzülmüş tüm satırları kapsar.</Text>
      </Space>
      {hataMetni && <Alert type="error" showIcon style={{ marginBottom: 10 }} message="Satırlar alınamadı" description={hataMetni} />}
      <Table rowKey="__k" size="small" loading={yukleniyor} columns={kolonlar} dataSource={satirlar}
        scroll={{ x: genislik, y: 620 }} onChange={degisti} showSorterTooltip={false}
        pagination={{
          current: sayfa, pageSize: boy, total: veri?.toplam_satir || 0, showSizeChanger: true,
          pageSizeOptions: [50, 100, 500], showTotal: (t, [a, b]) => `${sayi(a)}–${sayi(b)} / ${sayi(t)}`,
        }}
        locale={{ emptyText: <Empty description={yukleniyor ? 'Yükleniyor…' : 'Kayıt yok'} /> }} />
    </div>
  );
}
